import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { type CreativeRun, runChangeSummary, runChanges } from './creativeRuns.js';
import { whenCompleted } from './scoredBatch.js';

const BAR_SX = {
  gap: 1,
  minWidth: 280,
  alignItems: 'center',
  px: 1.5,
  py: 1,
  borderRadius: 3,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const STEP_SX = { border: '1px solid', borderColor: 'divider', borderRadius: 2 };

const DOT_SX = {
  p: 0,
  border: 0,
  height: 6,
  borderRadius: 999,
  transition: 'width .2s ease, background-color .2s ease',
};

export interface RunNavigatorProps {
  runs: CreativeRun[];
  at: number;
  busy?: boolean;
  onSelect: (at: number) => void;
}

export function RunNavigator({ runs, at, busy = false, onSelect }: RunNavigatorProps): ReactNode {
  const current = runs[at];
  if (runs.length < 2 || current === undefined) return null;

  const last = runs.length - 1;
  const changes = runChanges(current.selected, runs[at - 1]?.selected);
  const subtitle = [runChangeSummary(changes), whenCompleted(current.completedAt)]
    .filter((part) => part !== null && part !== '')
    .join(' · ');
  const goTo = (to: number): void => {
    if (!busy && to >= 0 && to <= last) onSelect(to);
  };

  const caption = (
    <Typography sx={{ fontSize: 11, color: 'text.muted', minHeight: 16 }}>{subtitle}</Typography>
  );

  return (
    <Stack direction="row" data-testid="run-navigator" sx={BAR_SX}>
      <IconButton
        aria-label="Previous run"
        size="small"
        disabled={busy || at === 0}
        onClick={() => goTo(at - 1)}
        sx={STEP_SX}
      >
        <ArrowBackOutlined sx={{ fontSize: 16 }} />
      </IconButton>

      <Stack sx={{ flex: 1, minWidth: 0, alignItems: 'center' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
          {`Run ${at + 1} of ${runs.length}${at === last ? ' · Latest' : ''}`}
        </Typography>
        {changes.length === 0 ? (
          caption
        ) : (
          <Tooltip
            arrow
            title={
              <Stack sx={{ gap: 0.5, py: 0.5 }}>
                {changes.map((change) => (
                  <Typography key={change.label} sx={{ fontSize: 11 }}>
                    <b>{change.label}:</b> {change.from} → {change.to}
                  </Typography>
                ))}
              </Stack>
            }
          >
            {caption}
          </Tooltip>
        )}
        <Stack direction="row" sx={{ gap: 0.6, mt: 1 }}>
          {runs.map((run, index) => (
            <Box
              component="button"
              key={run.runId}
              aria-label={`Go to run ${index + 1}`}
              disabled={busy}
              onClick={() => goTo(index)}
              sx={{
                ...DOT_SX,
                cursor: busy ? 'default' : 'pointer',
                width: index === at ? 16 : 6,
                bgcolor: index === at ? 'primary.main' : 'divider',
              }}
            />
          ))}
        </Stack>
      </Stack>

      <IconButton
        aria-label="Next run"
        size="small"
        disabled={busy || at === last}
        onClick={() => goTo(at + 1)}
        sx={STEP_SX}
      >
        <ArrowForwardOutlined sx={{ fontSize: 16 }} />
      </IconButton>
    </Stack>
  );
}
